import { ServerEvent } from "@/common/notification";
import {
	AgentState,
	useAgentStore,
} from "@/common/stores/persistingStores/agentPermissions";
import { notificationListener } from "@/hooks/notification";
import { useWhatsAppMessaging } from "@/hooks/whatsapp";
import { WhatsAppMessagesKey } from "@/repository/queryFns";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { TextEditor } from "./TextEditor";
import { MessagingInteractionContentProps } from "./types";
import { WhatsappMessageStatusUpdateNotification } from "@/common/models/whatsApp";
import { InteractionType } from "@/common/types";
import {
	useCurrentInteractionStore,
	CurrentInteractionState,
} from "@/common/stores/nonpersistingStores/currentInteraction";
import { MessagingChatLayout } from "./MessagingChatLayout";
import {
	useMessagingNotifications,
	useAddMessagesInChat,
	useHandleError,
} from "./useMessagingNotifications";

export const WhatsAppChatInteractionContent = (
	props: MessagingInteractionContentProps
) => {
	const { interactionId, isHistory = false } = props;

	const agentName = useAgentStore((state: AgentState) => state.fullName);
	const queryClient = useQueryClient();

	const currentInteraction = useCurrentInteractionStore(
		(state: CurrentInteractionState) => state.active
	);

	const [tooltipMountNode, setTooltipMountNode] =
		useState<HTMLDivElement | null>(null);

	const chatQueryKey = useMemo(
		() => [WhatsAppMessagesKey, "", {}, undefined, interactionId],
		[interactionId]
	);

	useMessagingNotifications(chatQueryKey);

	notificationListener<WhatsappMessageStatusUpdateNotification>(
		ServerEvent.WhatsAppMessageStatusUpdated,
		(notification: WhatsappMessageStatusUpdateNotification) => {
			queryClient.setQueryData(chatQueryKey, (oldData: any) => {
				const newData = {
					...oldData,
					pages: oldData.pages.map((page: any) => {
						return {
							...page,
							items: page.items.map((message: any) => {
								return message.id === notification.messageId
									? { ...message, status: notification.status }
									: message;
							}),
						};
					}),
				};
				
				return newData;
			});
		}
	);

	const addMessagesInChat = useAddMessagesInChat(chatQueryKey);
	const handleError = useHandleError(
		interactionId,
		InteractionType[InteractionType.WhatsApp].toString(),
		chatQueryKey
	);

	const messagingMethods = useWhatsAppMessaging(
		agentName,
		interactionId,
		isHistory,
		addMessagesInChat,
		handleError,
		tooltipMountNode
	);

	return (
		<MessagingChatLayout
			messagingMethods={messagingMethods}
			props={props}
			containerRef={setTooltipMountNode}
			sendBoxContent={
				<TextEditor
					sendMessages={messagingMethods.sendMessages}
					allowAttachments={true}
					isDisabled={currentInteraction?.isOnHold}
				/>
			}
		/>
	);
};
